import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TicketKanbanFilters as Filters } from "./types";
import { TICKET_PRIORITY_DISPLAY_NAMES } from "./constants";

const NONE = "__none__";

type Props = {
  filters: Filters;
  onChange: (next: Filters) => void;
  categories: Array<{ name: string; description: string }>;
  employees: Array<{ id: string; name: string }>;
};

export const TicketKanbanFilters = ({
  filters,
  onChange,
  categories,
  employees,
}: Props) => {
  const [phoneInput, setPhoneInput] = useState(filters.phone);
  const phoneDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const debouncedPhoneChange = useCallback(
    (value: string) => {
      clearTimeout(phoneDebounceRef.current);
      phoneDebounceRef.current = setTimeout(
        () => onChange({ ...filters, phone: value }),
        400
      );
    },
    [filters, onChange]
  );

  useEffect(() => () => clearTimeout(phoneDebounceRef.current), []);
  useEffect(() => setPhoneInput(filters.phone), [filters.phone]);

  const hasActiveFilters =
    filters.phone.length > 0 ||
    filters.category.length > 0 ||
    filters.assignedEmployeeId.length > 0 ||
    filters.priority.length > 0;

  const handleClear = () => {
    setPhoneInput("");
    clearTimeout(phoneDebounceRef.current);
    onChange({
      phone: "",
      startDate: filters.startDate,
      endDate: filters.endDate,
      category: "",
      assignedEmployeeId: "",
      priority: "",
    });
  };

  return (
    <div className="flex flex-col gap-2 border-b border-gray-200 bg-white px-3 py-2 md:flex-row md:flex-wrap md:items-center md:gap-3">
      {/* Phone search */}
      <div className="relative min-w-[180px] flex-1 md:max-w-[240px]">
        <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={phoneInput}
          onChange={(e) => {
            setPhoneInput(e.target.value);
            debouncedPhoneChange(e.target.value);
          }}
          placeholder="Buscar por teléfono"
          className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-8 text-sm outline-none placeholder:text-gray-400 focus:border-palomma-primary focus:ring-1 focus:ring-palomma-primary"
        />
        {phoneInput.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setPhoneInput("");
              clearTimeout(phoneDebounceRef.current);
              onChange({ ...filters, phone: "" });
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {/* Category */}
      <Select
        value={filters.category || NONE}
        onValueChange={(v) =>
          onChange({ ...filters, category: v === NONE ? "" : v })
        }
      >
        <SelectTrigger className="h-9 w-full text-sm md:w-[180px]">
          <SelectValue placeholder="Todas las categorías" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Todas las categorías</SelectItem>
          {categories.map((c) => (
            <SelectItem key={c.name} value={c.name}>
              {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Employee */}
      <Select
        value={filters.assignedEmployeeId || NONE}
        onValueChange={(v) =>
          onChange({ ...filters, assignedEmployeeId: v === NONE ? "" : v })
        }
      >
        <SelectTrigger className="h-9 w-full text-sm md:w-[180px]">
          <SelectValue placeholder="Todos los asesores" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Todos los asesores</SelectItem>
          {employees.map((e) => (
            <SelectItem key={e.id} value={e.id}>
              {e.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Priority */}
      <Select
        value={filters.priority || NONE}
        onValueChange={(v) =>
          onChange({ ...filters, priority: v === NONE ? "" : v })
        }
      >
        <SelectTrigger className="h-9 w-full text-sm md:w-[160px]">
          <SelectValue placeholder="Todas las prioridades" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Todas las prioridades</SelectItem>
          {Object.entries(TICKET_PRIORITY_DISPLAY_NAMES).map(
            ([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            )
          )}
        </SelectContent>
      </Select>

      {/* Date range */}
      <input
        type="date"
        value={filters.startDate}
        onChange={(e) =>
          onChange({ ...filters, startDate: e.target.value || filters.startDate })
        }
        className="h-9 rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-palomma-primary focus:ring-1 focus:ring-palomma-primary"
      />
      <input
        type="date"
        value={filters.endDate}
        onChange={(e) =>
          onChange({ ...filters, endDate: e.target.value || filters.endDate })
        }
        className="h-9 rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-palomma-primary focus:ring-1 focus:ring-palomma-primary"
      />

      {/* Clear */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleClear}
          className="h-9 shrink-0 rounded-md border border-gray-200 px-3 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
        >
          Limpiar
        </button>
      )}
    </div>
  );
};
